import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  Schedule as ScheduleIcon,
  // Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { generateReport, downloadReportById } from '../services/api';
import { ReportResponse, ReportTypeOption, ReportType } from '../types';
import { accent } from '../theme';

const REPORT_TYPES: ReportTypeOption[] = [
  {
    value: 'coral_compliance',
    label: 'Compliance Report',
    description: 'Comprehensive coral tracking and status report for regulatory compliance',
  },
];

// Helper function to format date for input[type="date"]
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date for display
const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType>('coral_compliance');
  const [dateFrom, setDateFrom] = useState<string>(
    formatDateForInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) // 30 days ago
  );
  const [dateTo, setDateTo] = useState<string>(formatDateForInput(new Date()));
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [generatedReport, setGeneratedReport] = useState<ReportResponse | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);

  const handleGenerateReport = async () => {
    if (!reportType || !dateFrom || !dateTo) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(dateFrom) > new Date(dateTo)) {
      setError('Start date must be before end date');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setDownloadError(null);
    setGeneratedReport(null);

    try {
      const report = await generateReport(reportType, dateFrom, dateTo);
      console.log('Generate report response:', report);
      if (report.expiresAt) {
        console.log('Download link expires at:', new Date(report.expiresAt).toLocaleString());
        console.log(
          'Time until expiry:',
          Math.round((new Date(report.expiresAt).getTime() - Date.now()) / 1000),
          'seconds'
        );
      }
      setGeneratedReport(report);
      setSuccess(`Report generation started successfully! Status: ${report.status}`);
    } catch (err: any) {
      setError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Always request a fresh presigned URL so users can re-download without regenerating the report
  const handleDownload = async () => {
    if (!generatedReport) {
      return;
    }

    setDownloadError(null);
    setDownloading(true);

    try {
      const downloadData = await downloadReportById(generatedReport.reportId);
      const link = document.createElement('a');
      link.href = downloadData.downloadUrl;
      link.download = `coral_${reportType}_${dateFrom}_to_${dateTo}.csv`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      setDownloadError(
        err.message || 'Failed to initiate download. Please try again or contact support.'
      );
    } finally {
      setDownloading(false);
    }
  };

  const selectedReportType = REPORT_TYPES.find((type) => type.value === reportType);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Generate comprehensive reports for coral tracking, compliance, and analytics.
      </Typography>

      <Paper sx={{ p: 4, mb: 3, ...accent.coral }}>
        <Typography variant="h6" gutterBottom>
          Generate New Report
        </Typography>
        <br />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Report Type, Date Range Selection, and Generate Button */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel id="report-type-label">Report Type</InputLabel>
              <Select
                labelId="report-type-label"
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value as ReportType)}
              >
                {REPORT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body1">{type.label}</Typography>
                      {/* <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography> */}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 180 }}
            />

            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={loading || !reportType || !dateFrom || !dateTo}
              startIcon={loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              size="large"
              sx={{ minWidth: 180 }}
            >
              {loading ? 'Generating Report...' : 'Generate Report'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Generated Report Card */}
      {generatedReport && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'success.main' }}>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" color="success.main" gutterBottom>
                  Report Ready for Download
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedReportType?.label}
                </Typography>
              </Box>
              <br />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Date Range:</strong> {formatDateForDisplay(dateFrom)} to{' '}
                {formatDateForDisplay(dateTo)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Report ID:</strong> {generatedReport.reportId}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Status:</strong>{' '}
                <span style={{ textTransform: 'capitalize' }}>{generatedReport.status}</span>
              </Typography>
              <Typography variant="body2">
                <strong>Estimated Completion:</strong>{' '}
                {new Date(generatedReport.estimatedCompletionTime).toLocaleString()}
              </Typography>
            </Box>
            <br />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
              {generatedReport.status === 'completed' ? (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={downloading}
                    size="large"
                  >
                    {downloading ? 'Requesting Link...' : 'Download CSV Report'}
                  </Button>

                  <Tooltip
                    title={
                      'Each download request generates a fresh secure link that expires shortly after use.'
                    }
                  >
                    <IconButton size="small">
                      <ScheduleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Button
                  variant="outlined"
                  disabled
                  startIcon={<CircularProgress size={20} />}
                  size="large"
                >
                  {generatedReport.status === 'processing'
                    ? 'Processing Report...'
                    : generatedReport.status === 'failed'
                      ? 'Report Generation Failed'
                      : 'Report Pending...'}
                </Button>
              )}
            </Box>

            {/* Show alert only for in-progress reports or download errors */}
            {(generatedReport.status !== 'completed' || downloadError) && (
              <Alert
                severity={downloadError ? 'error' : 'info'}
                sx={{ mt: 2 }}
                onClose={downloadError ? () => setDownloadError(null) : undefined}
              >
                <Typography variant="caption">
                  {downloadError ||
                    (generatedReport.status === 'processing'
                      ? 'Report generation is in progress. The download link will be available once processing is complete. Check back in a few moments or refresh the page.'
                      : generatedReport.status === 'failed'
                        ? 'Report generation failed. Please try generating the report again with different parameters or contact support if the issue persists.'
                        : 'Report is pending processing. Please wait for the generation to complete.')}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Help Information */}
      <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Report Information
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Generate detailed reports for coral tracking and compliance purposes. All reports are
          generated in CSV format for easy analysis and sharing.
        </Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Available Report Types:
        </Typography>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {REPORT_TYPES.map((type) => (
            <Box key={type.value} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{type.label}:</strong> {type.description}
              </Typography>
            </Box>
          ))}
        </ul>

        <Typography variant="caption" display="block" sx={{ mt: 2, fontStyle: 'italic' }}>
          Note: Reports are generated on-demand and may take a few moments for large date ranges.
          Download links are short-lived for security, but you can always click the Download button
          again to request a new link for the same report.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Reports;
